import { Component, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { Button } from '../ui/button';
import { RefreshCw } from 'lucide-react';
import { logger } from '../../lib/logger';
import { useTranslation } from '../../i18n';

/** Functional fallback UI to enable i18n hook usage */
function ErrorFallback({ onReset }: { onReset: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 p-8">
      <h2 className="text-xl font-semibold">{t('general.somethingWentWrong')}</h2>
      <p className="text-muted-foreground text-sm text-center max-w-md">
        {t('general.unexpectedError')}
      </p>
      <Button onClick={onReset} variant="outline">
        <RefreshCw className="mr-2 h-4 w-4" />
        {t('general.tryAgain')}
      </Button>
    </div>
  );
}

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class RouteErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    logger.error('RouteErrorBoundary caught error:', error);
    Sentry.captureException(error, { extra: errorInfo as Record<string, unknown> });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? (
        <ErrorFallback onReset={this.handleReset} />
      );
    }
    return this.props.children;
  }
}
